import * as _ from 'lodash';
import * as utility from '../misc/utility';
import * as EnumValues from '../misc/EnumValues';
import { BaseObject } from './BaseObject';

/**
 * @typedef { import('../server/ResponseContext').ResponseContext } ResponseContext
 */

/**
 * @typedef PersonEarning
 * @type {object}
 * @property {string} EmployerName
 * @property {number} UnitPrice
 * @property {Date} ValidFrom
 * @property {?Date} ValidTo
 */

export class Person extends BaseObject {
    constructor(obj) {
        super();

        if (_.isObjectLike(obj)) {
            _.assign(this, obj);

            this.ConsentDate = utility.parseDate(this.ConsentDate);
            this.LastLookupTimestamp = utility.parseDate(this.LastLookupTimestamp);
            this.PolitikerExpireDate = utility.parseDate(this.PolitikerExpireDate);
            this.PolitikerActivationTimestamp = utility.parseDate(this.PolitikerActivationTimestamp);
            this.ReportingDaysExceptionDate = utility.parseOptionalDate(this.ReportingDaysExceptionDate);

            this.firstName = this.PreferredName ? this.PreferredName : this.RegisteredName;

            var first = this.firstName ? (this.firstName + ' ') : '';
            var last = this.LastName || '';

            this.name = first + last;
            this.display = this.name;

            if (_.isArray(this.Addresses)) {
                this.Addresses.forEach(addr => {
                    if (addr.Type === EnumValues.AddressType.Home)
                        this.homeAddress = addr;
                    if (addr.Type === EnumValues.AddressType.Distribution)
                        this.distributionAddress = addr;
                });
            }

            if (_.isArray(this.PhoneNumbers)) {
                this.PhoneNumbers.forEach(phone => {
                    if (phone.Type === EnumValues.PhoneType.Home)
                        this.phoneHome = phone;
                    if (phone.Type === EnumValues.PhoneType.Mobile)
                        this.phoneMobile = phone;
                    if (phone.Type === EnumValues.PhoneType.Work)
                        this.phoneWork = phone;
                    if (phone.Type === EnumValues.PhoneType.Holiday)
                        this.phoneHoliday = phone;
                });
            }

            if (_.isArray(this.Emails)) {
                this.Emails.forEach(email => {
                    if (email.Type === EnumValues.EmailType.Public)
                        this.emailPublic = email;
                    if (email.Type === EnumValues.EmailType.Internal)
                        this.emailInternal = email;
                    if (email.Type === EnumValues.EmailType.Private)
                        this.emailPrivate = email;
                });
            }

            if (_.isArray(this.Earnings)) {
                this.Earnings.forEach(earning => {
                    earning.ValidFrom = utility.parseDate(earning.ValidFrom);
                    earning.ValidTo = utility.parseDate(earning.ValidTo);
                });
                this.Earnings = _.orderBy(this.Earnings, [x => x.ValidTo != null ? x.ValidTo.getTime() : Number.MAX_SAFE_INTEGER, x => x.ValidFrom, x => x.EmployerName], ['desc', 'desc', 'asc']);
            } else {
                this.Earnings = [];
            }
        }
    }

    get hasPolitikerNow() {
        return this.HasPolitikerAccess && (this.PolitikerExpireDate == null || this.PolitikerExpireDate >= utility.todayDate());
    }

    /**
     * @param {ResponseContext} ctx
     */
    resolve(ctx) {
        this.Party = ctx.getParty(this.PartyId);
        this.Division = ctx.getDivision(this.DivisionId);

        if (this.Party && this.Party.Abbreviation)
            this.display += " (" + this.Party.Abbreviation + ")";
    }

    toJSON() {
        var ret = super.toJSON();

        ret.PartyId = BaseObject.withRelated(ret, "Party");
        ret.DivisionId = BaseObject.withRelated(ret, "Division");

        ret.Addresses = [
            utility.addressToJson(this.homeAddress, EnumValues.AddressType.Home),
            utility.addressToJson(this.distributionAddress, EnumValues.AddressType.Distribution)
        ].filter(utility.isNotNull);

        ret.PhoneNumbers = [
            utility.phoneToJson(this.phoneHome, EnumValues.PhoneType.Home),
            utility.phoneToJson(this.phoneMobile, EnumValues.PhoneType.Mobile),
            utility.phoneToJson(this.phoneWork, EnumValues.PhoneType.Work),
            utility.phoneToJson(this.phoneHoliday, EnumValues.PhoneType.Holiday)
        ].filter(utility.isNotNull);

        ret.Emails = [
            utility.emailToJson(this.emailPublic, EnumValues.EmailType.Public),
            utility.emailToJson(this.emailInternal, EnumValues.EmailType.Internal),
            utility.emailToJson(this.emailPrivate, EnumValues.EmailType.Private)
        ].filter(utility.isNotNull);

        return ret;
    }

    clone() {
        var src = _.clone(this);
        src.Party = null;
        src.Division = null;

        var ret = _.cloneDeep(src);
        ret.Party = this.Party;
        ret.Division = this.Division;
        return ret;
    }

    initEdit() {
        this.emailPublic = this.emailPublic || { UseForMessages: true };
        this.emailInternal = this.emailInternal || { UseForMessages: true };
        this.emailPrivate = this.emailPrivate || { UseForMessages: true };
    }

    static compare(a, b) {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;

        return utility.compare(a.name, b.name) || utility.compare(a.Id, b.Id);
    }
}
